import React, { useCallback, useEffect, useMemo, useState } from "react";
import { OrderedSet } from "immutable";
import { AgGridReact } from "ag-grid-react";
import _ from "lodash";
import {
    CellClickedEvent,
    ColDef,
    ColumnApi,
    GridApi,
    GridReadyEvent,
    IToolPanel,
    SideBarDef,
    SelectionChangedEvent,
    RowClassParams,
    RowHeightParams,
} from "ag-grid-community";
import "ag-grid-enterprise";
import { ROW_HEIGHT } from "constants/product-collection";
import { DATATEST } from "constants/integration";
import productCollectionGridViewStyles from "components/product-collection/product-collection-views/product-collection-grid-view/product-collection-grid-view-styles";
import { aggFuncs, expandRows, getRowId, handleRowGroupOpened, selectRows } from "./helpers/sizingViewHelper";
import {
    autoGroupColumnDef,
    defaultColDef,
    getSideBar,
    getTemplate,
    gridComponents,
    gridOptions,
    icons,
} from "./helpers/sizingViewOptions";
import { IGridItem } from "helpers/legacy-cart/line-items/items-conversion-helper";
import { GridViewStoredState } from "helpers/porduct-collection-types";
import useGridStateHandlers from "./hooks/useGridStateHandlers";
import { IGridContext } from "components/shipment-review/types";
import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-balham.css";
import "styles/cart/sizingView.styl";
import { getDetailRowHeight, getGridViewDetailRendererParams } from "containers/legacy-cart/helpers";
import {
    InitialGroupOrderComparatorParams,
    IsGroupOpenByDefaultParams,
} from "ag-grid-community/dist/lib/entities/iCallbackParams";
import { useDebounced } from "hooks";
import { ILineItem } from "constants/order-types";
import { COLUMN_FIELDS } from "constants/sizingView";
import me from "models/me";
import { getRowClasses } from "helpers/tests";

export interface IProps {
    storedState: GridViewStoredState;
    items: IGridItem[];
    selectedRowIds: OrderedSet<string>;
    expandedRowIds: OrderedSet<string>;
    onBulkItemsSet: (ids: OrderedSet<string>, selectedItems: Array<Pick<ILineItem, "_id" | "product">>) => void;
    onRowExpandChange: (ids: OrderedSet<string>, allExpanded: boolean, allCollapsed: boolean) => void;
    onGridMount: (api: GridApi, columnApi: ColumnApi) => void;
    onStoredStateChange: (state: GridViewStoredState) => void;
    context: IGridContext;
    columnDefs: ColDef[];
    groupSortComparator?: (params: InitialGroupOrderComparatorParams) => number;
}

export const SizingViewGrid: React.FC<IProps> = ({
    storedState,
    expandedRowIds,
    selectedRowIds,
    items,
    context,
    onGridMount,
    onBulkItemsSet,
    onStoredStateChange,
    onRowExpandChange,
    groupSortComparator,
    columnDefs,
}) => {
    const [gridApi, setGridApi] = useState<GridApi>(null);
    const [gridColumnApi, setGridColumnApi] = useState<ColumnApi>(null);

    const sideBar = useMemo<SideBarDef>(
        () =>
            getSideBar({
                usePivotMode: me.hasPermission("can_use_pivot_mode"),
            }),
        []
    );
    const overlayLoadingTemplate = useMemo<string>(() => getTemplate.overlayLoadingTemplate(), []);
    const overlayNoRowsTemplate = useMemo<string>(() => getTemplate.overlayNoRowsTemplate(), []);
    const isMobileiOS = useMemo<boolean>(() => /iPad|iPhone/.test(window.navigator.userAgent), []);

    const handleGroupChanges = useCallback(
        () => handleRowGroupOpened(gridApi, onRowExpandChange),
        [gridApi, onRowExpandChange]
    );
    const onRowGroupOpened = useDebounced(handleGroupChanges);

    const handleRowSelectionChange = useCallback(
        (event: SelectionChangedEvent) => {
            const selectedNodes = event.api?.getSelectedNodes() || [];
            const selectedItems: Array<Pick<ILineItem, "_id" | "product">> = [];
            selectedNodes.forEach((node) => {
                const childNodes = node.group ? node.allLeafChildren : [node];
                childNodes.forEach((childNode) => {
                    selectedItems.push(
                        ..._.map(childNode.data?.entries, ({ cartLineItemId }) => ({
                            _id: cartLineItemId,
                            product: childNode.data?.productId,
                        }))
                    );
                });
            });
            onBulkItemsSet(
                OrderedSet(selectedNodes.map((node) => getRowId(node))),
                _.uniqBy(_.compact(selectedItems), "_id")
            );
        },
        [onBulkItemsSet]
    );

    // can be prevented in the collapse/expand click handler.
    const handleCellClicked = useCallback(
        ({ event, node }: CellClickedEvent) => !event.defaultPrevented && node.setExpanded(!node.expanded),
        []
    );
    const changeHandlers = useGridStateHandlers({
        gridColumnApi,
        gridApi,
        storedState,
        onStoredStateChange,
    });

    const onGridReady = useCallback(
        ({ api, columnApi }: GridReadyEvent) => {
            setGridApi(api);
            setGridColumnApi(columnApi);
            onGridMount?.(api, columnApi);

            if (!storedState) {
                api.closeToolPanel();
            }
        },
        [onGridMount, storedState]
    );

    // default grouping by Location in case of bulk ordering
    const isGroupOpenByDefault = useCallback(
        (params: IsGroupOpenByDefaultParams) =>
            params.field === COLUMN_FIELDS.LOCATION && !params.level && params.rowNode.leafGroup, // single grouping by Location
        []
    );

    const rowClassRules = useMemo(
        () => ({
            overordered: ({ node, data }: CastProp<RowClassParams, "data", IGridItem>): boolean =>
                node.group
                    ? _.some(_.flatMap(node.allLeafChildren, "data"), "options.isOverordered")
                    : data.options.isOverordered,
            hasError: ({ node, data }: CastProp<RowClassParams, "data", IGridItem>): boolean =>
                !node.group && data.options.hasError,
            product: ({ node }: CastProp<RowClassParams, "data", IGridItem>): boolean => !node.group,
        }),
        []
    );

    const getRowHeight = useCallback(
        ({ data, node }: RowHeightParams) => (node.detail ? getDetailRowHeight(data.detailsRowsCount) : ROW_HEIGHT),
        []
    );

    // custom handling to save the filters expanding state
    useEffect(() => {
        if (gridApi) {
            const filtersToolPanel: IToolPanel = gridApi.getToolPanelInstance("filters");
            filtersToolPanel?.getGui().addEventListener("click", changeHandlers.onToolPanelVisibleChanged);
            return () => {
                filtersToolPanel?.getGui().removeEventListener("click", changeHandlers.onToolPanelVisibleChanged);
            };
        }
    }, [gridApi, changeHandlers.onToolPanelVisibleChanged]);

    useEffect(() => {
        if (gridApi && gridColumnApi) {
            changeHandlers.onFirstDataRendered(); // reset toolpanel filters view when changing columns
        }
    }, [columnDefs]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        gridApi && expandRows(gridApi, expandedRowIds);
    }, [expandedRowIds, gridApi]);

    useEffect(() => {
        gridApi && selectRows(gridApi, selectedRowIds, items?.length);
    }, [selectedRowIds, gridApi, items?.length]);

    return (
        <div
            {...productCollectionGridViewStyles()}
            className="ag-theme-balham sizing-view"
            data-test={DATATEST.SHIPMENT_REVIEW.SIZING_VIEW.GRID_WRAPPER}
        >
            <AgGridReact
                onGridReady={onGridReady}
                rowData={items}
                detailCellRendererParams={getGridViewDetailRendererParams}
                onSelectionChanged={handleRowSelectionChange}
                onRowGroupOpened={onRowGroupOpened}
                onColumnPivotModeChanged={changeHandlers.onColumnPivotModeChanged}
                {...changeHandlers}
                onCellClicked={handleCellClicked}
                autoGroupColumnDef={autoGroupColumnDef}
                isGroupOpenByDefault={isGroupOpenByDefault}
                rowClassRules={rowClassRules}
                getRowClass={getRowClasses}
                getRowId={getRowId}
                overlayLoadingTemplate={overlayLoadingTemplate}
                overlayNoRowsTemplate={overlayNoRowsTemplate}
                getRowHeight={getRowHeight}
                defaultColDef={defaultColDef}
                components={gridComponents}
                context={context}
                aggFuncs={aggFuncs}
                columnDefs={columnDefs}
                sideBar={sideBar}
                icons={icons}
                gridOptions={gridOptions}
                singleClickEdit={isMobileiOS}
                tooltipShowDelay={200}
                rowHeight={ROW_HEIGHT}
                rowSelection="multiple"
                initialGroupOrderComparator={groupSortComparator}
                allowDragFromColumnsToolPanel
                suppressColumnMoveAnimation
                suppressCopyRowsToClipboard
                debounceVerticalScrollbar
                suppressRowClickSelection // if true, clicking rows doesn't select (useful for checkbox selection)
                enableCellChangeFlash
                groupSelectsChildren
                suppressCellFocus
                masterDetail
                valueCache
                debug={false}
            />
        </div>
    );
};
